import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Calculator, TrendingUp, DollarSign, BarChart3, Info, 
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp, LineChart,
  Scale, Target, Percent, HelpCircle, PiggyBank, Settings, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiConfig';

// ============ Tooltips for financial terminology ============
const TOOLTIPS = {
  revenue: "Money PeCash earns from its service fee.",
  variableCost: "Costs directly associated with processing a transaction.",
  contribution: "Service-fee revenue remaining after variable transaction costs.",
  contributionMargin: "Contribution divided by service-fee revenue.",
  operatingProfit: "Contribution remaining after fixed operating expenses.",
  breakEven: "Number of transactions needed to cover fixed costs."
};

// ============ Helper Components ============
const Tooltip = ({ text }) => (
  <span className="group relative inline-block ml-1">
    <HelpCircle className="w-3.5 h-3.5 text-slate-500 inline cursor-help" />
    <span className="invisible group-hover:visible absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-700 text-xs text-slate-200 rounded-lg whitespace-nowrap z-50 max-w-xs">
      {text}
    </span>
  </span>
);

const CurrencyInput = ({ value, onChange, label, placeholder, disabled, status }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-400 flex items-center gap-1">
      {label}
      {status === 'NOT_YET_CONFIGURED' && (
        <span className="text-amber-500 text-xs">(Unknown)</span>
      )}
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        placeholder={placeholder || '0.00'}
        disabled={disabled}
        className={`w-full pl-8 pr-4 py-2 bg-slate-700 border rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
          status === 'NOT_YET_CONFIGURED' 
            ? 'border-amber-600/50' 
            : status === 'USER_ENTERED_ZERO' 
              ? 'border-blue-600/50' 
              : 'border-slate-600'
        }`}
      />
    </div>
  </div>
);

const StatCard = ({ label, value, subValue, icon: Icon, color = 'purple', tooltip }) => (
  <div className={`bg-slate-800 border border-slate-700 rounded-xl p-4`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 rounded-lg bg-${color}-900/50 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 text-${color}-400`} />
      </div>
      <span className="text-slate-400 text-sm">
        {label}
        {tooltip && <Tooltip text={tooltip} />}
      </span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
  </div>
);

export default function AdminPricingSimulator() {
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('pecash_token');
  
  // State
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('calculator');
  const [pricingCurve, setPricingCurve] = useState(null);
  const [competitorData, setCompetitorData] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFixedCosts, setShowFixedCosts] = useState(false);
  
  // Simulation inputs
  const [purchaseAmount, setPurchaseAmount] = useState(100);
  const [fxRate, setFxRate] = useState(60.0);
  const [variableCosts, setVariableCosts] = useState({
    payment_processing_cost: null,
    regulated_partner_cost: null,
    dr_settlement_cost: null,
    fraud_loss_cost: null,
    chargeback_refund_cost: null,
    variable_support_cost: null,
    merchant_incentive_cost: null,
    other_variable_cost: null,
  });
  const [fixedCosts, setFixedCosts] = useState({
    payroll: null,
    technology: null,
    legal_compliance: null,
    insurance: null,
    marketing: null,
    office_admin: null,
    customer_support_fixed: null,
    other_fixed: null,
  });

  // Fetch pricing curve on mount
  useEffect(() => {
    const fetchData = async () => {
      const token = getToken();
      try {
        const [curveRes, compRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/pricing-simulator/pricing-curve`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/pricing-simulator/competitor-reference`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (curveRes.ok) {
          const curveData = await curveRes.json();
          setPricingCurve(curveData);
        }
        
        if (compRes.ok) {
          const compData = await compRes.json();
          setCompetitorData(compData);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load pricing data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate simulation
  const calculateSimulation = useCallback(async () => {
    const token = getToken();
    setCalculating(true);
    
    try {
      const hasFixedCosts = Object.values(fixedCosts).some(v => v !== null);
      
      const endpoint = hasFixedCosts 
        ? '/api/admin/pricing-simulator/calculate-with-fixed'
        : '/api/admin/pricing-simulator/calculate';
      
      const body = hasFixedCosts 
        ? {
            name: 'Current Scenario',
            variable_costs: {
              purchase_amount: purchaseAmount,
              purchase_conversion_rate: fxRate,
              ...variableCosts
            },
            fixed_costs: fixedCosts
          }
        : {
            purchase_amount: purchaseAmount,
            purchase_conversion_rate: fxRate,
            ...variableCosts
          };
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error('Calculation failed');
      }
      
      const data = await response.json();
      setCalculationResult(data);
      
    } catch (err) {
      console.error('Calculation error:', err);
      toast.error('Failed to calculate simulation');
    } finally {
      setCalculating(false);
    }
  }, [purchaseAmount, fxRate, variableCosts, fixedCosts]);

  // Auto-calculate when amount changes
  useEffect(() => {
    if (purchaseAmount >= 10 && purchaseAmount <= 200) {
      const timer = setTimeout(() => {
        calculateSimulation();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [purchaseAmount, calculateSimulation]);

  // Quick amount buttons
  const quickAmounts = [10, 20, 30, 50, 100, 150, 200];

  // Render pricing curve chart (simple SVG)
  const renderPricingChart = useMemo(() => {
    if (!pricingCurve?.curve_points) return null;
    
    const points = pricingCurve.curve_points;
    const width = 600;
    const height = 250;
    const padding = { top: 20, right: 40, bottom: 40, left: 60 };
    
    const minAmount = Math.min(...points.map(p => p.amount));
    const maxAmount = Math.max(...points.map(p => p.amount));
    const minFee = Math.min(...points.map(p => p.fee));
    const maxFee = Math.max(...points.map(p => p.fee));
    
    const xScale = (val) => padding.left + ((val - minAmount) / (maxAmount - minAmount)) * (width - padding.left - padding.right);
    const yScale = (val) => height - padding.bottom - ((val - minFee) / (maxFee - minFee)) * (height - padding.top - padding.bottom);
    
    const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.amount)} ${yScale(p.fee)}`).join(' ');
    
    // Find current amount position
    const currentPoint = points.find(p => p.amount === purchaseAmount) || 
      points.reduce((prev, curr) => Math.abs(curr.amount - purchaseAmount) < Math.abs(prev.amount - purchaseAmount) ? curr : prev);
    
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-64">
        {/* Grid lines */}
        {[2.5, 3, 3.5, 4, 4.5, 5, 5.5].map(fee => (
          <line
            key={fee}
            x1={padding.left}
            y1={yScale(fee)}
            x2={width - padding.right}
            y2={yScale(fee)}
            stroke="#374151"
            strokeDasharray="4"
          />
        ))}
        
        {/* X-axis labels */}
        {quickAmounts.map(amount => (
          <text
            key={amount}
            x={xScale(amount)}
            y={height - 10}
            fill="#9CA3AF"
            fontSize="10"
            textAnchor="middle"
          >
            ${amount}
          </text>
        ))}
        
        {/* Y-axis labels */}
        {[3, 3.5, 4, 4.5, 5].map(fee => (
          <text
            key={fee}
            x={padding.left - 10}
            y={yScale(fee) + 4}
            fill="#9CA3AF"
            fontSize="10"
            textAnchor="end"
          >
            ${fee.toFixed(2)}
          </text>
        ))}
        
        {/* Pricing curve line */}
        <path
          d={pathData}
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="2"
        />
        
        {/* Reference points */}
        {points.filter(p => p.is_reference_point).map(point => (
          <circle
            key={point.amount}
            cx={xScale(point.amount)}
            cy={yScale(point.fee)}
            r="5"
            fill="#8B5CF6"
            stroke="#1E1B4B"
            strokeWidth="2"
          />
        ))}
        
        {/* Current amount highlight */}
        {purchaseAmount >= 10 && purchaseAmount <= 200 && calculationResult && (
          <>
            <line
              x1={xScale(purchaseAmount)}
              y1={padding.top}
              x2={xScale(purchaseAmount)}
              y2={height - padding.bottom}
              stroke="#22C55E"
              strokeDasharray="4"
            />
            <circle
              cx={xScale(purchaseAmount)}
              cy={yScale(calculationResult.result?.pecash_service_fee || currentPoint.fee)}
              r="8"
              fill="#22C55E"
              stroke="#14532D"
              strokeWidth="2"
            />
          </>
        )}
        
        {/* Axis labels */}
        <text x={width / 2} y={height - 2} fill="#9CA3AF" fontSize="11" textAnchor="middle">
          Purchase Amount (USD)
        </text>
        <text 
          x={15} 
          y={height / 2} 
          fill="#9CA3AF" 
          fontSize="11" 
          textAnchor="middle"
          transform={`rotate(-90, 15, ${height / 2})`}
        >
          Service Fee (USD)
        </text>
      </svg>
    );
  }, [pricingCurve, purchaseAmount, calculationResult]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading pricing simulator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
              data-testid="pricing-simulator-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Pricing Simulator</h1>
              <p className="text-sm text-slate-400">READ-ONLY Analytics Tool</p>
            </div>
          </div>
          
          {/* PILOT MODE badge */}
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-1.5">
            <span className="text-amber-400 text-sm font-medium">
              SIMULATED (PILOT MODE)
            </span>
          </div>
        </div>
      </header>

      {/* Safety Banner */}
      <div className="max-w-7xl mx-auto px-6 mt-4">
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-300 text-sm font-medium">Analytics Only - No Financial Mutations</p>
              <p className="text-slate-400 text-xs mt-1">
                This simulator cannot create authorizations, trigger payments, change pricing, or modify any financial state.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'calculator', label: 'Calculator', icon: Calculator },
            { id: 'curve', label: 'Pricing Curve', icon: LineChart },
            { id: 'projections', label: 'Projections', icon: BarChart3 },
            { id: 'competitor', label: 'Competitor Ref', icon: Scale },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? 'bg-purple-600 hover:bg-purple-700' 
                : 'text-slate-400 hover:text-white hover:bg-slate-800'}
              data-testid={`pricing-simulator-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="space-y-6">
              {/* Purchase Amount */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Purchase Amount
                </h3>
                
                {/* Quick Amount Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {quickAmounts.map(amount => (
                    <button
                      key={amount}
                      onClick={() => setPurchaseAmount(amount)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        purchaseAmount === amount
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                      data-testid={`pricing-simulator-amount-${amount}`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                
                {/* Custom Amount Input */}
                <div className="space-y-1">
                  <label className="text-sm text-slate-400">Custom Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">$</span>
                    <input
                      type="number"
                      min="10"
                      max="200"
                      step="0.01"
                      value={purchaseAmount}
                      onChange={(e) => setPurchaseAmount(parseFloat(e.target.value) || 0)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="pricing-simulator-amount-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Pilot range: $10 - $200</p>
                </div>
                
                {/* FX Rate */}
                <div className="mt-4">
                  <label className="text-sm text-slate-400">Purchase Conversion Rate (USD → DOP)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={fxRate}
                    onChange={(e) => setFxRate(parseFloat(e.target.value) || 60)}
                    className="w-full px-4 py-2 mt-1 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Variable Costs */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between text-white font-semibold"
                >
                  <span className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-purple-400" />
                    Variable Transaction Costs
                    <Tooltip text={TOOLTIPS.variableCost} />
                  </span>
                  {showAdvanced ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                <AnimatePresence>
                  {showAdvanced && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-700">
                        <CurrencyInput
                          label="Payment Processing"
                          value={variableCosts.payment_processing_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, payment_processing_cost: v})}
                          status={variableCosts.payment_processing_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Regulated Partner"
                          value={variableCosts.regulated_partner_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, regulated_partner_cost: v})}
                          status={variableCosts.regulated_partner_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="DR Settlement"
                          value={variableCosts.dr_settlement_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, dr_settlement_cost: v})}
                          status={variableCosts.dr_settlement_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Fraud/Loss"
                          value={variableCosts.fraud_loss_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, fraud_loss_cost: v})}
                          status={variableCosts.fraud_loss_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Chargeback/Refund"
                          value={variableCosts.chargeback_refund_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, chargeback_refund_cost: v})}
                          status={variableCosts.chargeback_refund_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Variable Support"
                          value={variableCosts.variable_support_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, variable_support_cost: v})}
                          status={variableCosts.variable_support_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Merchant Incentive"
                          value={variableCosts.merchant_incentive_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, merchant_incentive_cost: v})}
                          status={variableCosts.merchant_incentive_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                        <CurrencyInput
                          label="Other Variable"
                          value={variableCosts.other_variable_cost}
                          onChange={(v) => setVariableCosts({...variableCosts, other_variable_cost: v})}
                          status={variableCosts.other_variable_cost === null ? 'NOT_YET_CONFIGURED' : 'CONFIGURED'}
                        />
                      </div>
                      
                      <p className="text-xs text-amber-500 mt-4 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Costs with "Unknown" label are NOT YET CONFIGURED and excluded from calculations
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Fixed Costs (Optional) */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <button
                  onClick={() => setShowFixedCosts(!showFixedCosts)}
                  className="w-full flex items-center justify-between text-white font-semibold"
                >
                  <span className="flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-purple-400" />
                    Monthly Fixed Costs (Optional)
                  </span>
                  {showFixedCosts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                <AnimatePresence>
                  {showFixedCosts && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-700">
                        <CurrencyInput
                          label="Payroll"
                          value={fixedCosts.payroll}
                          onChange={(v) => setFixedCosts({...fixedCosts, payroll: v})}
                        />
                        <CurrencyInput
                          label="Technology"
                          value={fixedCosts.technology}
                          onChange={(v) => setFixedCosts({...fixedCosts, technology: v})}
                        />
                        <CurrencyInput
                          label="Legal/Compliance"
                          value={fixedCosts.legal_compliance}
                          onChange={(v) => setFixedCosts({...fixedCosts, legal_compliance: v})}
                        />
                        <CurrencyInput
                          label="Insurance"
                          value={fixedCosts.insurance}
                          onChange={(v) => setFixedCosts({...fixedCosts, insurance: v})}
                        />
                        <CurrencyInput
                          label="Marketing"
                          value={fixedCosts.marketing}
                          onChange={(v) => setFixedCosts({...fixedCosts, marketing: v})}
                        />
                        <CurrencyInput
                          label="Office/Admin"
                          value={fixedCosts.office_admin}
                          onChange={(v) => setFixedCosts({...fixedCosts, office_admin: v})}
                        />
                        <CurrencyInput
                          label="Customer Support"
                          value={fixedCosts.customer_support_fixed}
                          onChange={(v) => setFixedCosts({...fixedCosts, customer_support_fixed: v})}
                        />
                        <CurrencyInput
                          label="Other Fixed"
                          value={fixedCosts.other_fixed}
                          onChange={(v) => setFixedCosts({...fixedCosts, other_fixed: v})}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={calculateSimulation}
                disabled={calculating || purchaseAmount < 10 || purchaseAmount > 200}
                className="w-full bg-purple-600 hover:bg-purple-700 py-3"
                data-testid="pricing-simulator-calculate-btn"
              >
                {calculating ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate Simulation
                  </>
                )}
              </Button>
            </div>

            {/* Results Panel */}
            <div className="space-y-6">
              {calculationResult ? (
                <>
                  {/* Summary Card */}
                  <div className="bg-gradient-to-br from-purple-900/50 to-slate-800 border border-purple-700/50 rounded-2xl p-6">
                    <h3 className="text-white font-semibold mb-4">Pricing Summary</h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">Purchase Amount</span>
                        <span className="text-2xl font-bold text-white" data-testid="pricing-simulator-purchase-amount">
                          ${calculationResult.result?.purchase_amount?.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">PeCash Service Fee</span>
                        <span className="text-2xl font-bold text-purple-400" data-testid="pricing-simulator-service-fee">
                          ${calculationResult.result?.pecash_service_fee?.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="h-px bg-slate-700" />
                      
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300 font-medium">Customer Total</span>
                        <span className="text-3xl font-bold text-emerald-400" data-testid="pricing-simulator-customer-total">
                          ${calculationResult.result?.customer_total?.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="bg-slate-800/50 rounded-lg p-3 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Fee as % of Purchase</span>
                          <span className="text-white">{calculationResult.result?.fee_as_pct_of_purchase}%</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                          <span className="text-slate-400">Conversion Rate</span>
                          <span className="text-white">{calculationResult.result?.purchase_conversion_rate} DOP/USD</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                          <span className="text-slate-400">DOP Purchasing Availability</span>
                          <span className="text-white font-medium">
                            RD${calculationResult.result?.dop_purchasing_availability?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-xs text-slate-500 mt-4">
                      Service fee is NOT included in beneficiary purchasing availability
                    </p>
                  </div>

                  {/* Unit Economics */}
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                      Unit Economics
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <StatCard
                        label="Revenue"
                        value={`$${calculationResult.result?.revenue_per_transaction?.toFixed(2)}`}
                        icon={DollarSign}
                        color="emerald"
                        tooltip={TOOLTIPS.revenue}
                      />
                      <StatCard
                        label="Variable Cost"
                        value={`$${calculationResult.result?.total_variable_transaction_cost?.toFixed(2)}`}
                        icon={Settings}
                        color="amber"
                        tooltip={TOOLTIPS.variableCost}
                      />
                      <StatCard
                        label="Contribution"
                        value={`$${calculationResult.result?.contribution_per_transaction?.toFixed(2)}`}
                        icon={Target}
                        color="purple"
                        tooltip={TOOLTIPS.contribution}
                      />
                      <StatCard
                        label="Margin"
                        value={`${calculationResult.result?.contribution_margin_pct?.toFixed(1)}%`}
                        icon={Percent}
                        color="blue"
                        tooltip={TOOLTIPS.contributionMargin}
                      />
                    </div>
                    
                    {calculationResult.result?.break_even_transactions_per_month && (
                      <div className="mt-4 p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300 flex items-center gap-1">
                            Break-Even Transactions/Month
                            <Tooltip text={TOOLTIPS.breakEven} />
                          </span>
                          <span className="text-xl font-bold text-white">
                            {calculationResult.result.break_even_transactions_per_month.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Warnings */}
                  {calculationResult.warnings?.filter(Boolean).length > 0 && (
                    <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-300 font-medium text-sm">Simulation Warnings</p>
                          <ul className="text-amber-200/80 text-sm mt-1 space-y-1">
                            {calculationResult.warnings.filter(Boolean).map((warning, i) => (
                              <li key={i}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center">
                  <Calculator className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Enter a purchase amount to see pricing calculations</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pricing Curve Tab */}
        {activeTab === 'curve' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-purple-400" />
              PeCash Pricing Curve
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Linear interpolation between reference points. Purple dots are reference points.
              Green line shows currently selected amount.
            </p>
            
            <div className="bg-slate-900 rounded-xl p-4">
              {renderPricingChart}
            </div>
            
            {/* Reference Points Table */}
            <div className="mt-6">
              <h4 className="text-white font-medium mb-3">Reference Points</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400">Amount</th>
                      <th className="text-left py-3 px-4 text-slate-400">Service Fee</th>
                      <th className="text-left py-3 px-4 text-slate-400">Customer Total</th>
                      <th className="text-left py-3 px-4 text-slate-400">Fee %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingCurve?.reference_points?.map((point, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-white font-medium">${point.amount_usd}</td>
                        <td className="py-3 px-4 text-purple-400">${point.service_fee_usd}</td>
                        <td className="py-3 px-4 text-emerald-400">${point.customer_total_usd}</td>
                        <td className="py-3 px-4 text-slate-300">{point.fee_as_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Projections Tab */}
        {activeTab === 'projections' && calculationResult?.result?.projections && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Scale Projections at ${purchaseAmount}/transaction
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400">Transactions</th>
                    <th className="text-right py-3 px-4 text-slate-400">GMV</th>
                    <th className="text-right py-3 px-4 text-slate-400">Fee Revenue</th>
                    <th className="text-right py-3 px-4 text-slate-400">Variable Costs</th>
                    <th className="text-right py-3 px-4 text-slate-400">Contribution</th>
                  </tr>
                </thead>
                <tbody>
                  {calculationResult.result.projections.map((proj, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4 text-white font-medium">
                        {proj.transactions.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300">
                        ${proj.gmv.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400">
                        ${proj.service_fee_revenue.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-amber-400">
                        ${proj.variable_transaction_costs.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-purple-400 font-medium">
                        ${proj.contribution.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="text-xs text-slate-500 mt-4">
              CONTRIBUTION is not NET PROFIT. Net/operating profit requires fixed operating expenses.
            </p>
          </div>
        )}

        {/* Competitor Reference Tab */}
        {activeTab === 'competitor' && competitorData && (
          <div className="space-y-6">
            {/* Warning Banner */}
            <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-300 font-semibold">OWNER-SUPPLIED MARKET OBSERVATIONS</p>
                  <p className="text-amber-200/80 text-sm mt-1">NOT LIVE COMPETITOR PRICING</p>
                  <p className="text-slate-400 text-xs mt-2">{competitorData.disclaimer}</p>
                </div>
              </div>
            </div>
            
            {/* Comparison Table */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-400" />
                PeCash vs Competitor (Observed Fees)
              </h3>
              
              <p className="text-slate-400 text-sm mb-4">
                Current PeCash target at reference points is $1.00 below these observed fees.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400">Amount</th>
                      <th className="text-right py-3 px-4 text-slate-400">Competitor Fee</th>
                      <th className="text-right py-3 px-4 text-slate-400">PeCash Fee</th>
                      <th className="text-right py-3 px-4 text-slate-400">Customer Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorData.competitor_points.map((point, i) => (
                      <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-white font-medium">${point.amount}</td>
                        <td className="py-3 px-4 text-right text-red-400">${point.competitor_fee}</td>
                        <td className="py-3 px-4 text-right text-purple-400">
                          {point.pecash_fee ? `$${point.pecash_fee}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-right text-emerald-400 font-medium">
                          {point.customer_savings ? `$${point.customer_savings}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <p className="text-xs text-slate-500 mt-4">
                Do NOT claim PeCash is always $1 cheaper because competitor pricing may change.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
